import {
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  ViewChild,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { Observable, Subject, of, isObservable, from } from "rxjs";
import { map, takeUntil, take } from "rxjs/operators";
import { UploadFile, DeleteFileFn } from "../types/upload-area.types";
import { DsNotificationService } from "../../services/ds-notification.service";
import { DocumentsService } from "@nexu-library/utilities";

type View = "mobile" | "desktop";

@Component({
  selector: "ds-upload-area",
  templateUrl: "./ds-upload-area.component.html",
  styleUrls: ["./ds-upload-area.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsUploadAreaComponent),
      multi: true,
    },
  ],
})
export class DsUploadAreaComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() multiple = false;
  @Input() disabled = false;
  @Input() required = false;
  @Input() accept = "";
  @Input() capture = "";
  @Input() error = false;
  @Input() errorMessage = "";
  @Input() size = "lg";
  @Input() loading = false;
  @Input() loadingText = "Cargando archivo...";
  @Input() title = "Selecciona, arrastra o pega aquí tus archivos";
  @Input() titleMobile = "";
  @Input() description = "";
  @Input() enablePaste = true;
  @Input() uploadButtonLabel = "Cargar";
  @Input() successMessage = "Carga completa";
  @Input() alreadyUploadedMessage = "Archivo previamente cargado";
  @Input() errorButtonLabel = "Cargar otro archivo";
  @Input() maxFiles?: number;
  @Input() deleteFile?: DeleteFileFn;
  @Input() retryLabel = "Reintentar";
  @Input() enableRetry = false;
  @Input() hidePreview = false;
  @Input() showReplaceForUploaded = false;
  @Input() hideFileList = false;
  @Input() hideDelete = false;
  @Input() hideReplace = false;
  @Input() previewLabel = "Ver";
  @Input() replaceLabel = "Reemplazar";
  @Input() deleteLabel = "Eliminar";
  /** When true, the small success state shows labeled buttons instead of the compact icon actions. */
  @Input() labeledActions = false;
  @Input() successIcon = "check-line";
  @Input() uploadIcon = "upload";
  @Input() deleteIcon = "thrash-line";
  @Input() blockDropzone = false;
  @Input() successIconType = "success";
  /** When true, the upload area fills the host height (useful in horizontal equal-height rows). */
  @HostBinding("class.ds-upload-area--stretch") @Input() stretchHeight = false;
  @Output() retry: EventEmitter<UploadFile> = new EventEmitter<UploadFile>();

  @Output() previewRequested = new EventEmitter<{ index: number; item: UploadFile }>();

  @ViewChild("fileUpload") fileUpload!: ElementRef<HTMLInputElement>;

  filesConfig: UploadFile[] = [];
  hover_dropzone = false;
  status: string;
  icon: string;
  isMobile = false;

  private replaceIndex: number | null = null;
  private onChange = (value: UploadFile[]) => {};
  private onTouched = () => {};
  private destroy$ = new Subject<void>();

  readonly sizeMap: Record<View, "sm" | "md" | "lg"> = {
    mobile: "md",
    desktop: "lg",
  };

  readonly sizeMapSmall: Record<View, "sm" | "md"> = {
    mobile: "sm",
    desktop: "md",
  };

  isMobile$: Observable<boolean>;
  btnSize$: Observable<"sm" | "md" | "lg">;
  btnSizeSmall$: Observable<"sm" | "md">;

  constructor(
    private elementRef: ElementRef,
    private breakpointObserver: BreakpointObserver,
    private dsNotificationService: DsNotificationService,
    private documentsService: DocumentsService
  ) {
    this.isMobile$ = this.breakpointObserver.observe([Breakpoints.Handset]).pipe(
      map((result) => {
        const element = this.elementRef.nativeElement;
        if (element.classList.contains("force-mobile")) {
          return true;
        }
        if (element.classList.contains("force-desktop")) {
          return false;
        }
        return result.matches;
      })
    );

    this.btnSize$ = this.isMobile$.pipe(map((isMobile) => (isMobile ? this.sizeMap.mobile : this.sizeMap.desktop)));

    this.btnSizeSmall$ = this.isMobile$.pipe(
      map((isMobile) => (isMobile ? this.sizeMapSmall.mobile : this.sizeMapSmall.desktop))
    );
  }

  ngOnInit(): void {
    this.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile = isMobile;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get displayTitleMobile(): string {
    return this.titleMobile || this.title;
  }

  writeValue(value: UploadFile[] | File[]): void {
    if (!value) {
      this.filesConfig = [];
      return;
    }
    if (!Array.isArray(value)) {
      this.filesConfig = [];
      return;
    }
    this.filesConfig = value.map((item) => ("file" in item ? (item as UploadFile) : { file: item as File }));
  }

  registerOnChange(fn: (value: UploadFile[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onEmptyAreaClick(event: Event, showUpload: boolean, disabledUpload?: boolean): void {
    if (this.disabled || disabledUpload) {
      return;
    }
    if (!(this.filesConfig.length === 0 || showUpload)) {
      return;
    }
    // Interactive elements handle their own click; the empty state icon is decorative and must stay clickable.
    const target = event.target as HTMLElement | null;
    if (target?.closest("ds-button, button, a")) {
      return;
    }
    this.fileUpload?.nativeElement?.click();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (this.disabled) return;
    if ((this.loading || this.blockDropzone) && this.filesConfig.length > 0) return;
    this.hover_dropzone = true;
  }

  onDragLeave(event: DragEvent) {
    if (this.disabled) return;
    if ((this.loading || this.blockDropzone) && this.filesConfig.length > 0) return;
    this.hover_dropzone = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.hover_dropzone = false;
    if (this.disabled) return;
    if ((this.loading || this.blockDropzone) && this.filesConfig.length > 0) return;
    const files = event.dataTransfer?.files;
    if (files) {
      this.addSelectedFiles(Array.from(files).map((file) => ({ file })));
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (!this.enablePaste || this.disabled) return;
    if ((this.loading || this.blockDropzone) && this.filesConfig.length > 0) return;

    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const pastedFiles: UploadFile[] = [];
    for (const item of Array.from(clipboardData.items)) {
      if (item.kind !== "file") continue;

      const blob = item.getAsFile();
      if (!blob) continue;

      const extension = this.getPastedFileExtension(blob);
      const name = `${this.getPastedFileName()}${extension}`;
      const file = new File([blob], name, { type: blob.type });

      if (!this.isFileAccepted(file)) {
        continue;
      }

      pastedFiles.push({ file });
    }

    if (pastedFiles.length === 0) {
      this.dsNotificationService.open({
        title: "Contenido no válido",
        message: "Error al procesar el contenido pegado.",
        type: "warning",
        time: 5000,
      });
      return;
    }

    event.preventDefault();
    this.addSelectedFiles(pastedFiles);
  }

  private addSelectedFiles(selectedFiles: UploadFile[]): void {
    if (selectedFiles.length === 0) {
      if (!this.multiple) {
        this.filesConfig = this.applyFileLimits(selectedFiles);
        this.onChange(this.filesConfig);
        this.onTouched();
      }
      return;
    }

    if (this.multiple) {
      this.processReplaceFiles(selectedFiles);
      return;
    }

    this.filesConfig = this.applyFileLimits(selectedFiles);
    this.onChange(this.filesConfig);
    this.onTouched();
  }

  private getPastedFileExtension(blob: File): string {
    const nameExtension = blob.name.includes(".") ? blob.name.split(".").pop() : "";
    if (nameExtension && /^[a-z0-9]+$/i.test(nameExtension)) {
      return `.${nameExtension.toLowerCase()}`;
    }

    const mimeExtensions: Record<string, string> = {
      "image/png": ".png",
      "image/jpeg": ".jpg",
      "image/jpg": ".jpg",
      "image/gif": ".gif",
      "image/webp": ".webp",
      "image/bmp": ".bmp",
      "image/svg+xml": ".svg",
      "image/tiff": ".tiff",
      "image/tif": ".tif",
    };

    return mimeExtensions[blob.type] ?? ".png";
  }

  private isFileAccepted(file: File): boolean {
    const acceptTokens = this.parseAcceptTokens();
    if (acceptTokens.length === 0) {
      return true;
    }

    const fileType = this.normalizeMimeType(file.type);
    const fileName = file.name.toLowerCase();

    return acceptTokens.some((token) => {
      if (token.startsWith(".")) {
        return fileName.endsWith(token);
      }
      if (token.endsWith("/*")) {
        return fileType.startsWith(token.slice(0, -1));
      }
      return fileType === this.normalizeMimeType(token);
    });
  }

  private parseAcceptTokens(): string[] {
    if (!this.accept?.trim()) {
      return [];
    }

    return this.accept
      .split(",")
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean);
  }

  private normalizeMimeType(mimeType: string): string {
    const normalized = mimeType.toLowerCase();
    return normalized === "image/jpg" ? "image/jpeg" : normalized;
  }

  private getPastedFileName(): string {
    return new Date()
      .toLocaleString("default", {
        year: "2-digit",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(/,/g, "")
      .replace(/ |:/g, "_");
  }

  onFileChange(event: Event) {
    if (this.disabled) return;
    const files = (event.target as HTMLInputElement).files;
    if (!files) return;
    const selectedFiles = Array.from(files).map((file) => ({ file }));

    if (this.replaceIndex !== null) {
      this.processReplaceFiles(selectedFiles, this.replaceIndex);
      this.replaceIndex = null;
    } else {
      if (this.multiple) {
        this.processReplaceFiles(selectedFiles);
      } else {
        this.filesConfig = this.applyFileLimits(selectedFiles);
      }
      this.onChange(this.filesConfig);
      this.onTouched();
    }
    (event.target as HTMLInputElement).value = "";
  }

  clearFile() {
    if (this.disabled) return;

    this.executeWithBeforeRemove(this.filesConfig[0], () => {
      this.fileUpload.nativeElement.value = "";
      this.filesConfig = [];
      this.onChange(this.filesConfig);
      this.onTouched();
    });
  }

  removeFile(index: number) {
    if (this.disabled) return;

    const fileToRemove = this.filesConfig[index];
    if (!fileToRemove) return;

    this.executeWithBeforeRemove(fileToRemove, () => {
      this.filesConfig.splice(index, 1);
      this.onChange(this.filesConfig);
      this.onTouched();
    });
  }

  private executeWithBeforeRemove(files: UploadFile | UploadFile[], removeFn: () => void): void {
    if (!this.deleteFile) {
      removeFn();
      return;
    }
    const result = this.deleteFile(files);
    if (typeof result === "boolean") {
      if (result) {
        removeFn();
      }
      return;
    }
    const result$ = isObservable(result) ? result : from(result);

    result$.pipe(take(1), takeUntil(this.destroy$)).subscribe((shouldRemove) => {
      if (shouldRemove) {
        removeFn();
      }
    });
  }

  replaceFile(index: number) {
    if (this.disabled || index < 0 || index >= this.filesConfig.length) {
      return;
    }

    this.replaceIndex = index;
    this.fileUpload.nativeElement.click();
  }

  private processReplaceFiles(selectedFiles: UploadFile[], index?: number) {
    if (selectedFiles.length === 0) {
      return;
    }
    const updatedFiles = [...this.filesConfig];
    const isFileDuplicate = (uploadFile: UploadFile): boolean =>
      updatedFiles.some(
        (existingFile, i) =>
          (!index || i !== index) &&
          existingFile.file.name === uploadFile.file.name &&
          existingFile.file.size === uploadFile.file.size
      );
    const newFiles = selectedFiles.filter((uploadFile) => !isFileDuplicate(uploadFile));
    const duplicateCount = selectedFiles.length - newFiles.length;
    if (duplicateCount > 0) {
      this.dsNotificationService.open({
        title: `${
          duplicateCount > 1
            ? "Se detectaron archivos duplicados"
            : selectedFiles.length > 1
            ? "Se detectó un archivo duplicado"
            : "Archivo duplicado"
        }`,
        message: "Solo se agregarán archivos nuevos.",
        type: "warning",
        time: 5000,
      });
    }

    if (newFiles.length === 0) {
      return;
    }
    if (index !== undefined) {
      updatedFiles[index] = newFiles[0];

      if (newFiles.length > 1) {
        updatedFiles.push(...newFiles.slice(1));
      }
    } else {
      updatedFiles.push(...newFiles);
    }

    this.filesConfig = this.applyFileLimits(updatedFiles);
    this.onChange(this.filesConfig);
    this.onTouched();
  }

  private applyFileLimits(files: UploadFile[]): UploadFile[] {
    if (!this.multiple) {
      return files.length === 0 ? [] : [files[0]];
    }
    if (!this.maxFiles) {
      return files;
    }
    return files.slice(0, this.maxFiles);
  }

  retryUpload(index: number) {
    this.retry.emit(this.filesConfig[index]);
  }

  previewFile(index: number) {
    if (this.disabled) return;

    const item: any = this.filesConfig[index];
    if (!item) return;

    if (this.previewRequested.observers.length > 0) {
      this.previewRequested.emit({ index, item });
      return;
    }

    const meta = item?.alreadyUploaded && item?.docId ? item : item?.file;

    // File from server
    if (meta?.alreadyUploaded && meta?.docId) {
      this.documentsService
        .download(meta.docId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (blob: any) => {
            const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
            window.open(url, "_blank", "noopener,noreferrer");
          },
          error: () => {
            this.dsNotificationService.open({
              title: "No se pudo previsualizar el archivo",
              message: "Ocurrió un error al descargar el archivo para previsualización.",
              type: "danger",
              time: 5000,
            });
          },
        });
      return;
    }

    // Current file uploaded
    if (item?.file instanceof Blob) {
      try {
        const url = URL.createObjectURL(item.file);
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (error) {
        this.dsNotificationService.open({
          title: "No se pudo previsualizar el archivo",
          message: "Error al crear la vista previa del archivo.",
          type: "danger",
          time: 5000,
        });
      }
    }
  }
}
